import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Spinner, Button } from 'reactstrap';
import RoleSelection from './components/RoleSelection';
import FirstRequest from './components/FirstRequest';
import PlanSelection from './components/PlanSelection';
import DebugToolWidgetResults from '../../components/DebugToolWidget/DebugToolWidgetResults';
import { TrackPostHogEvent } from '../../shared/utils/posthog';
import { updateUserType, sendSegmentEvent } from '../../redux/actions/users';
import { createSegmentEvent } from '../../redux/segment';
import { ONBOARDING_ROLE_SELECTED, ONBOARDING_COMPLETED } from '../../redux/actionTypes';
import './OnboardingFlow.scss';

const OnboardingFlow = ({ history, user, debuggerForm, plans, currentOrg, updateUserType, sendSegmentEvent }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState(null);
  const [hasCompletedFirstRequest, setHasCompletedFirstRequest] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hasSelectedPlan, setHasSelectedPlan] = useState(false);
  const [selectedPlanType, setSelectedPlanType] = useState(null);
  const [initialRequestCount, setInitialRequestCount] = useState(0);
  const [onboardingStartTime, setOnboardingStartTime] = useState(null);
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);
  const [trialTier, setTrialTier] = useState(null);

  // Refs for component callbacks
  const roleSelectionRef = useRef(null);
  const planSelectionRef = useRef(null);
  const allPlansRef = useRef(null);

  // Track onboarding entry
  useEffect(() => {
    const startTime = new Date();
    setOnboardingStartTime(startTime);
    
    TrackPostHogEvent('onboarding_started', {
      entry_time: startTime.toISOString(),
      user_id: user && user.id,
      user_email: user && user.email,
      user_signup_date: user && user.createdAt,
      current_plan: user && user.organizations && user.organizations[0] && user.organizations[0].plans && user.organizations[0].plans[0] && user.organizations[0].plans[0].tier && user.organizations[0].plans[0].tier.name || 'Unknown',
      current_plan_price: user && user.organizations && user.organizations[0] && user.organizations[0].plans && user.organizations[0].plans[0] && user.organizations[0].plans[0].tier && user.organizations[0].plans[0].tier.priceMonthly || 0,
      existing_request_count: currentOrg && currentOrg.currentBillingCycle && currentOrg.currentBillingCycle.totalCalls || 0,
      is_marketing_user: user && user.isMarketing || false,
      session_id: `onboarding_${user && user.id}_${Date.now()}`
    });
  }, [user, currentOrg]);

  // Track step progression
  useEffect(() => {
    if (currentStep > 1) {
      TrackPostHogEvent('onboarding_step_reached', {
        step: currentStep,
        step_name: getStepName(currentStep),
        time_since_start: onboardingStartTime ? new Date() - onboardingStartTime : null,
        previous_step: currentStep - 1,
        user_id: user && user.id,
        selected_role: selectedRole,
        session_id: `onboarding_${user && user.id}_${Date.now()}`
      });
    }
  }, [currentStep, onboardingStartTime, user, selectedRole]);

  // Track initial request count when user starts step 2
  useEffect(() => {
    if (currentStep === 2 && currentOrg && currentOrg.currentBillingCycle && typeof currentOrg.currentBillingCycle.totalCalls === 'number') {
      setInitialRequestCount(currentOrg.currentBillingCycle.totalCalls);
    }
  }, [currentStep, currentOrg]);

  // Watch for actual request count increases (not just form completion)
  useEffect(() => {
    if (currentStep === 2 && currentOrg && currentOrg.currentBillingCycle && typeof currentOrg.currentBillingCycle.totalCalls === 'number') {
      const currentRequestCount = currentOrg.currentBillingCycle.totalCalls;
      
      // Check if request count has actually increased since starting step 2
      if (currentRequestCount > initialRequestCount) {
        setHasCompletedFirstRequest(true);
      }
    }
  }, [currentStep, currentOrg, initialRequestCount]);

  // Watch for successful plan creation/update to redirect to dashboard
  useEffect(() => {
    if (hasSelectedPlan && selectedPlanType === 'paid' && plans.detailPlan && !plans.loading) {
      // Track paid plan completion
      TrackPostHogEvent('onboarding_completed', {
        completion_type: 'paid_plan_completed',
        selected_role: selectedRole,
        selected_plan: plans.detailPlan && plans.detailPlan.tier && plans.detailPlan.tier.name || 'Unknown',
        plan_price: plans.detailPlan && plans.detailPlan.tier && plans.detailPlan.tier.priceMonthly || 0,
        final_step: 3,
        total_time: onboardingStartTime ? new Date() - onboardingStartTime : null,
        user_id: user && user.id,
        requests_made: currentOrg && currentOrg.currentBillingCycle && currentOrg.currentBillingCycle.totalCalls || 0,
        redirect_destination: '/apis/Opengraphio',
        session_id: `onboarding_${user && user.id}_${Date.now()}`
      });

      // Send Active Campaign event via Segment for onboarding completion (identify - user trait)
      sendSegmentEvent({
        userId: user && user.id,
        onboardingCompleted: true,
        selectedRole: selectedRole
      }, 'identify');
      
      // For paid plans, wait for successful plan update then redirect
      setTimeout(() => {
        history.push('/apis/Opengraphio');
      }, 1000); // Small delay to show success state
    }
  }, [hasSelectedPlan, selectedPlanType, plans.detailPlan, plans.loading, history, selectedRole, onboardingStartTime, user, currentOrg]);

  // Track onboarding abandonment (user leaves page before completion)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!hasSelectedPlan) {
        TrackPostHogEvent('onboarding_abandoned', {
          abandonment_step: currentStep,
          step_name: getStepName(currentStep),
          selected_role: selectedRole,
          time_spent: onboardingStartTime ? new Date() - onboardingStartTime : null,
          user_id: user && user.id,
          requests_made: currentOrg && currentOrg.currentBillingCycle && currentOrg.currentBillingCycle.totalCalls || 0,
          session_id: `onboarding_${user && user.id}_${Date.now()}`
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentStep, selectedRole, hasSelectedPlan, onboardingStartTime, user, currentOrg]);

  const getStepName = (step) => {
    switch (step) {
      case 1: return 'role_selection';
      case 2: return 'first_request';
      case 3: return 'plan_selection';
      default: return 'unknown';
    }
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    
    // Track role selection in PostHog
    TrackPostHogEvent('onboarding_role_selected', {
      selected_role: role,
      time_since_start: onboardingStartTime ? new Date() - onboardingStartTime : null,
      is_marketing_selection: role === 'marketer',
      step: 1,
      user_id: user && user.id,
      session_id: `onboarding_${user && user.id}_${Date.now()}`
    });

    // Send Active Campaign event for role selection (identify - user trait)
    sendSegmentEvent({
      userId: user && user.id,
      selectedRole: role
    }, 'identify');
  };

  const handleRoleContinue = async () => {
    if (selectedRole && !isUpdatingRole) {
      setIsUpdatingRole(true);
      
      // Track continue button click
      TrackPostHogEvent('onboarding_role_continue_clicked', {
        selected_role: selectedRole,
        requires_db_update: selectedRole === 'marketer',
        timestamp: new Date().toISOString()
      });
      
      // Only update user type in database if marketer is selected
      if (selectedRole === 'marketer') {
        debugger;
        try {
          await updateUserType(true);
        } catch (error) {
          console.error('Error updating user type:', error);
          alert('Error updating user type, please try again. If the problem persists, please contact support.');
          setIsUpdatingRole(false);
          return;
        }
        // Handle marketer flow - redirect to dashboard
        TrackPostHogEvent('onboarding_completed', {
          completion_type: 'marketer_redirect',
          selected_role: selectedRole,
          final_step: 1,
          total_time: onboardingStartTime ? new Date() - onboardingStartTime : null,
          user_id: user && user.id,
          redirect_destination: '/apis/Opengraphio',
          session_id: `onboarding_${user && user.id}_${Date.now()}`
        });

        // Send Active Campaign event for onboarding completion (identify - user trait)
        sendSegmentEvent({
          userId: user && user.id,
          onboardingCompleted: true,
          selectedRole: selectedRole
        }, 'identify');
        
        history.push('/apis/Opengraphio');
      } else if (selectedRole === 'developer') {
        
        TrackPostHogEvent('onboarding_developer_progression', {
          selected_role: selectedRole,
          time_since_start: onboardingStartTime ? new Date() - onboardingStartTime : null,
          next_step: 2,
          user_id: user && user.id,
          session_id: `onboarding_${user && user.id}_${Date.now()}`
        });
        
        // Start smooth transition to step 2
        setIsTransitioning(true);
        
        // Smooth transition with timing
        setTimeout(() => {
          setCurrentStep(2);
          setTimeout(() => {
            setIsTransitioning(false);
          }, 300); // Allow content to load before removing transition state
        }, 800); // Show transition message for 800ms
      }
      
      setIsUpdatingRole(false);
    }
  };

  const handleFirstRequestComplete = () => {
    setHasCompletedFirstRequest(true);
    
    // Track successful first request
    TrackPostHogEvent('onboarding_first_request_completed', {
      time_since_start: onboardingStartTime ? new Date() - onboardingStartTime : null,
      step: 2,
      request_count_increase: currentOrg && currentOrg.currentBillingCycle && currentOrg.currentBillingCycle.totalCalls - initialRequestCount,
      final_request_count: currentOrg && currentOrg.currentBillingCycle && currentOrg.currentBillingCycle.totalCalls || 0,
      session_id: `onboarding_${user && user.id}_${Date.now()}`
    });
  };

  const handleNextToPlanSelection = () => {
    // Track progression to plan selection
    TrackPostHogEvent('onboarding_proceeding_to_plans', {
      time_since_start: onboardingStartTime ? new Date() - onboardingStartTime : null,
      step: 2,
      next_step: 3,
      requests_made: currentOrg && currentOrg.currentBillingCycle && currentOrg.currentBillingCycle.totalCalls || 0,
      session_id: `onboarding_${user && user.id}_${Date.now()}`
    });
    
    setCurrentStep(3);
  };

  const handlePlanSelected = (planType, tier = null) => {
    
    // Track plan selection
    TrackPostHogEvent('onboarding_plan_selected', {
      plan_type: planType,
      tier_name: tier && tier.name || 'Unknown',
      tier_price: tier && tier.priceMonthly || 0,
      is_trial: tier && tier.isTrial || false,
      is_free_plan: tier && tier.priceMonthly === 0,
      time_since_start: onboardingStartTime ? new Date() - onboardingStartTime : null,
      step: 3,
      requests_made: currentOrg && currentOrg.currentBillingCycle && currentOrg.currentBillingCycle.totalCalls || 0,
    });
    
    // Mark that user has selected a plan during onboarding
    setHasSelectedPlan(true);
    
    // For free plans or if no tier specified, redirect immediately
    if (!tier || tier.priceMonthly === 0) {
      setSelectedPlanType('free');
      
      // Track free plan completion
      TrackPostHogEvent('onboarding_completed', {
        completion_type: 'free_plan_selected',
        selected_role: selectedRole,
        selected_plan: tier && tier.name || 'Free',
        final_step: 3,
        total_time: onboardingStartTime ? new Date() - onboardingStartTime : null,
        requests_made: currentOrg && currentOrg.currentBillingCycle && currentOrg.currentBillingCycle.totalCalls || 0,
        redirect_destination: '/apis/Opengraphio',
      });

      // Send Active Campaign event for onboarding completion (identify - user trait)
      sendSegmentEvent({
        userId: user && user.id,
        onboardingCompleted: true,
        selectedRole: selectedRole
      }, 'identify');
      
      history.push('/apis/Opengraphio');
    } else {
      // For paid plans, set the type and let the modal handle the flow
      setSelectedPlanType('paid');
      
      // Track paid plan initiation
      TrackPostHogEvent('onboarding_paid_plan_initiated', {
        selected_role: selectedRole,
        selected_plan: tier && tier.name || 'Unknown',
        plan_price: tier && tier.priceMonthly || 0,
        time_since_start: onboardingStartTime ? new Date() - onboardingStartTime : null,
        requests_made: currentOrg && currentOrg.currentBillingCycle && currentOrg.currentBillingCycle.totalCalls || 0,
      });
      
      // The useEffect above will redirect after successful plan creation
    }
  };

  const renderLeftColumn = () => {
    if (isTransitioning) {
      return (
        <div className="onboarding-transition-content">
          <div className="transition-message">
            <div className="transition-icon">
            <Spinner size="sm" color="primary" className="ml-2" />
            </div>
            <h3>Setting up your developer experience...</h3>
            <p>Getting ready to test the API</p>
          </div>
        </div>
      );
    }

    switch (currentStep) {
      case 1:
        return <RoleSelection 
          onRoleSelect={handleRoleSelect} 
          selectedRole={selectedRole}
        />;
      case 2:
        return (
          <FirstRequest 
            onFirstRequestComplete={handleFirstRequestComplete}
            onNext={handleNextToPlanSelection}
            hasCompletedFirstRequest={hasCompletedFirstRequest}
          />
        );
      case 3:
        return <PlanSelection 
          onPlanSelected={handlePlanSelected}
          onTrialTierLoaded={setTrialTier}
          allPlansRef={allPlansRef}
        />;
      default:
        return <RoleSelection 
          onRoleSelect={handleRoleSelect} 
          selectedRole={selectedRole}
        />;
    }
  };

  const renderLeftFooter = () => {
    if (isTransitioning) {
      return null;
    }

    switch (currentStep) {
      case 1:
        return (
          <div className="onboarding-footer">
            <Button 
              color="primary" 
              className="footer-button role-next-button" 
              onClick={handleRoleContinue}
              disabled={!selectedRole || isUpdatingRole}
            >
              Continue →
            </Button>
          </div>
        );
      case 2:
        return (
          <div className="onboarding-footer">
            {hasCompletedFirstRequest && (
              <Button 
                color="success" 
                className="footer-button continue-button"
                onClick={handleNextToPlanSelection}
                disabled={debuggerForm.loading}
              >
                Continue to Plans →
              </Button>
            )}
          </div>
        );
      case 3:
        return null; // No footer for step 3
      default:
        return null;
    }
  };

  const renderRightColumn = () => {
    if (isTransitioning) {
      return (
        <div className="onboarding-right-empty">
          <div className="onboarding-empty-state transition-right-content">
            <div className="transition-progress">
              <div className="progress-dots">
                <span className="dot active"></span>
                <span className="dot animating"></span>
                <span className="dot"></span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    switch (currentStep) {
      case 1:
        return (
          <></>
        );
      case 2:
        return (
          <div className="onboarding-right-results">
            {debuggerForm.loading ? (
              <div className="first-request-loading-state">
                <div className="loading-spinner-container">
                  <div className="loading-spinner"></div>
                </div>
              </div>
            ) : !(debuggerForm && debuggerForm.site && debuggerForm.site.results) ? (
              <></>
            ) : (
              <>
                {hasCompletedFirstRequest && (
                  <div className="first-request-success">
                    <div className="success-message">
                      <span className="success-icon">✅</span>
                      <h4>You just made your first request!</h4>
                    </div>
                  </div>
                )}
                <DebugToolWidgetResults />
              </>
            )}
          </div>
        );
      case 3:
        return (
          <div className="onboarding-right-empty">
            <div className="onboarding-empty-state">
              <h4>Almost there!</h4>
              <p>Choose your plan to unlock the full dashboard experience.</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };


  return (
    <div className="onboarding-flow">
      <div className="onboarding-container">
        <div className="onboarding-row">
          <div className="onboarding-left-column">
            <div className="onboarding-left-content">
              <div className="onboarding-header">
                <h1 className="onboarding-logo">
                  OpenGraph<span style={{color: '#2CBD6B'}}>.</span>io
                </h1>
              </div>
              <div className={`onboarding-left-scroll step-${currentStep}`}>
                <div className={`onboarding-content-wrapper ${isTransitioning ? 'transitioning' : ''} step-${currentStep}`}>
                  {renderLeftColumn()}
                </div>
              </div>
              {renderLeftFooter()}
            </div>
          </div>
          <div className="onboarding-right-column">
            <div className="onboarding-right-content">
              <div className={`onboarding-content-wrapper ${isTransitioning ? 'transitioning' : ''} step-${currentStep}`}>
                {renderRightColumn()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  updateUserType,
  sendSegmentEvent,
};


const mapStateToProps = (state) => ({
  user: state.users.user,
  debuggerForm: state.debuggerForm,
  plans: state.plans,
  currentOrg: state.organization.currentOrg,
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(OnboardingFlow));